import React, { useEffect, useMemo, useState } from "react";
import {
  Alert,
  Box,
  Button,
  Card,
  CardContent,
  CircularProgress,
  Checkbox,
  Divider,
  FormControlLabel,
  Grid,
  Snackbar,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  TextField,
  Typography,
} from "@mui/material";
import { alpha, useTheme } from "@mui/material/styles";
import subscriptionService from "../../../services/subscriptionService";

export default function SuperAdminPlans() {
  const theme = useTheme();

  const [loading, setLoading] = useState(true);
  const [plans, setPlans] = useState([]);

  const [form, setForm] = useState({
    code: "",
    name: "",
    monthly_price_tl: "",
    doctor_limit: "",
    monthly_email_limit: "",
    storage_limit_gb: "",
    is_active: true,
  });

  const [snackbar, setSnackbar] = useState({ open: false, message: "", severity: "success" });

  const [billingForm, setBillingForm] = useState({});
  const [billingMsg, setBillingMsg] = useState(null);
  const [billingErr, setBillingErr] = useState(null);
  const [billingSaving, setBillingSaving] = useState(false);

  const refresh = async () => {
    setLoading(true);
    try {
      const res = await subscriptionService.getSuperAdminPlans();
      setPlans(res?.plans || []);
    } catch (e) {
      setSnackbar({
        open: true,
        message: e?.response?.data?.error || e?.response?.data?.message || e.message || "Failed to load plans",
        severity: "error",
      });
    } finally {
      setLoading(false);
    }
  };

  const loadBilling = async () => {
    setBillingErr(null);
    try {
      const data = await subscriptionService.getSuperAdminBillingSettings();
      const s = data.settings || data;
      setBillingForm({
        addon_price_tl_monthly: s.addon_price_tl_monthly,
        addon_doctor_slots_per_unit: s.addon_doctor_slots_per_unit,
        addon_extra_storage_gb_per_unit: s.addon_extra_storage_gb_per_unit,
        addon_extra_email_monthly_per_unit: s.addon_extra_email_monthly_per_unit,
        yearly_discount_percent: s.yearly_discount_percent,
      });
    } catch (e) {
      setBillingErr(e.response?.data?.error || "Platform fiyat ayarları yüklenemedi");
    }
  };

  const saveBilling = async () => {
    setBillingSaving(true);
    setBillingMsg(null);
    setBillingErr(null);
    try {
      await subscriptionService.putSuperAdminBillingSettings(billingForm);
      setBillingMsg("Platform fiyat ayarları kaydedildi");
      await loadBilling();
    } catch (e) {
      setBillingErr(e.response?.data?.error || "Kayıt hatası");
    } finally {
      setBillingSaving(false);
    }
  };

  useEffect(() => {
    refresh();
    loadBilling();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const currentCode = form.code ? String(form.code).toUpperCase() : "";

  const handleLoadPlan = (p) => {
    setForm({
      code: p.code || "",
      name: p.name || "",
      monthly_price_tl: p.monthly_price_tl || "",
      doctor_limit: p.doctor_limit || "",
      monthly_email_limit: p.monthly_email_limit || "",
      storage_limit_gb: p.storage_limit_gb || "",
      is_active: !!p.is_active,
    });
  };

  const handleSave = async () => {
    try {
      const payload = {
        name: form.name,
        monthly_price_tl: form.monthly_price_tl,
        doctor_limit: form.doctor_limit,
        monthly_email_limit: form.monthly_email_limit,
        storage_limit_gb: form.storage_limit_gb,
        is_active: !!form.is_active,
      };
      await subscriptionService.upsertSuperAdminPlan(currentCode, payload);
      setSnackbar({ open: true, message: "Plan saved", severity: "success" });
      await refresh();
    } catch (e) {
      setSnackbar({
        open: true,
        message: e?.response?.data?.error || e?.response?.data?.message || e.message || "Save failed",
        severity: "error",
      });
    }
  };

  const plansSorted = useMemo(() => plans || [], [plans]);

  return (
    <Box sx={{ p: 3 }}>
      <Typography variant="h4" sx={{ fontWeight: 800, color: "text.primary", mb: 2 }}>
        Paketler ve ek kapasite
      </Typography>
      <Typography variant="body2" sx={{ color: "text.secondary", mb: 3 }}>
        Abonelik planlarını (limit ve fiyat) ve platform genelinde geçerli ek paket birimi ile yıllık faturalama indirimini
        buradan yönetin.
      </Typography>

      {loading ? (
        <Box sx={{ display: "flex", justifyContent: "center", py: 6 }}>
          <CircularProgress color="primary" />
        </Box>
      ) : (
        <Grid container spacing={3} sx={{ mb: 1 }}>
          <Grid item xs={12} md={6}>
            <Card
              sx={{
                borderRadius: 4,
                border: "1px solid",
                borderColor: "divider",
                bgcolor: "background.paper",
                boxShadow: (t) =>
                  t.palette.mode === "dark" ? `0 8px 24px ${alpha("#000", 0.35)}` : `0 8px 24px ${alpha("#0f172a", 0.06)}`,
              }}
            >
              <CardContent>
                <Typography variant="h6" sx={{ fontWeight: 800, color: "text.primary", mb: 1 }}>
                  Mevcut planlar
                </Typography>

                <Box sx={{ overflowX: "auto" }}>
                  <Table size="small">
                    <TableHead>
                      <TableRow>
                        <TableCell>Code</TableCell>
                        <TableCell>Name</TableCell>
                        <TableCell>Doctor Limit</TableCell>
                        <TableCell>Storage (GB)</TableCell>
                        <TableCell align="right">Active</TableCell>
                        <TableCell />
                      </TableRow>
                    </TableHead>
                    <TableBody>
                      {plansSorted.map((p) => (
                        <TableRow key={p.id}>
                          <TableCell sx={{ fontWeight: 700 }}>{p.code}</TableCell>
                          <TableCell>{p.name}</TableCell>
                          <TableCell>{p.doctor_limit}</TableCell>
                          <TableCell>{p.storage_limit_gb}</TableCell>
                          <TableCell align="right">{p.is_active ? "Yes" : "No"}</TableCell>
                          <TableCell>
                            <Button variant="outlined" size="small" onClick={() => handleLoadPlan(p)}>
                              Edit
                            </Button>
                          </TableCell>
                        </TableRow>
                      ))}
                      {plansSorted.length === 0 && (
                        <TableRow>
                          <TableCell colSpan={6}>
                            <Typography sx={{ color: "text.secondary" }}>No plans found.</Typography>
                          </TableCell>
                        </TableRow>
                      )}
                    </TableBody>
                  </Table>
                </Box>
              </CardContent>
            </Card>
          </Grid>

          <Grid item xs={12} md={6}>
            <Card
              sx={{
                borderRadius: 4,
                border: "1px solid",
                borderColor: "divider",
                bgcolor: "background.paper",
                boxShadow: (t) =>
                  t.palette.mode === "dark" ? `0 8px 24px ${alpha("#000", 0.35)}` : `0 8px 24px ${alpha("#0f172a", 0.06)}`,
              }}
            >
              <CardContent>
                <Typography variant="h6" sx={{ fontWeight: 800, color: "text.primary", mb: 2 }}>
                  Plan oluştur / güncelle
                </Typography>

                <Grid container spacing={2}>
                  <Grid item xs={12}>
                    <TextField
                      fullWidth
                      label="Code (e.g. CORP_1)"
                      size="small"
                      value={form.code}
                      onChange={(e) => setForm((s) => ({ ...s, code: e.target.value }))}
                    />
                  </Grid>
                  <Grid item xs={12}>
                    <TextField
                      fullWidth
                      label="Name"
                      size="small"
                      value={form.name}
                      onChange={(e) => setForm((s) => ({ ...s, name: e.target.value }))}
                    />
                  </Grid>
                  <Grid item xs={6}>
                    <TextField
                      fullWidth
                      label="Monthly price TL"
                      size="small"
                      type="number"
                      value={form.monthly_price_tl}
                      onChange={(e) => setForm((s) => ({ ...s, monthly_price_tl: e.target.value }))}
                    />
                  </Grid>
                  <Grid item xs={6}>
                    <TextField
                      fullWidth
                      label="Doctor limit"
                      size="small"
                      type="number"
                      value={form.doctor_limit}
                      onChange={(e) => setForm((s) => ({ ...s, doctor_limit: e.target.value }))}
                    />
                  </Grid>
                  <Grid item xs={6}>
                    <TextField
                      fullWidth
                      label="Monthly email limit"
                      size="small"
                      type="number"
                      value={form.monthly_email_limit}
                      onChange={(e) => setForm((s) => ({ ...s, monthly_email_limit: e.target.value }))}
                    />
                  </Grid>
                  <Grid item xs={6}>
                    <TextField
                      fullWidth
                      label="Storage limit GB"
                      size="small"
                      type="number"
                      value={form.storage_limit_gb}
                      onChange={(e) => setForm((s) => ({ ...s, storage_limit_gb: e.target.value }))}
                    />
                  </Grid>
                  <Grid item xs={12}>
                    <FormControlLabel
                      control={
                        <Checkbox
                          checked={!!form.is_active}
                          onChange={(e) => setForm((s) => ({ ...s, is_active: e.target.checked }))}
                        />
                      }
                      label="Active"
                    />
                  </Grid>
                  <Grid item xs={12}>
                    <Button
                      fullWidth
                      variant="contained"
                      onClick={handleSave}
                      disabled={!currentCode}
                      sx={{
                        borderRadius: 3,
                        py: 1.3,
                        fontWeight: 800,
                        background: `linear-gradient(135deg, ${theme.palette.secondary.main} 0%, ${theme.palette.primary.main} 100%)`,
                      }}
                    >
                      Save
                    </Button>
                  </Grid>
                </Grid>
              </CardContent>
            </Card>
          </Grid>
        </Grid>
      )}

      <Grid container spacing={3}>
        <Grid item xs={12}>
          <Typography variant="h6" sx={{ fontWeight: 800, color: "text.primary", mb: 1 }}>
            Platform fiyat ayarları
          </Typography>
          <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
            Ek kapasite birimi tanımı ayrı; yıllık indirim ise paket + eklerin toplamında (yıllık ödeme seçildiğinde) uygulanır.
          </Typography>
          {billingMsg && (
            <Alert severity="success" sx={{ mb: 2 }} onClose={() => setBillingMsg(null)}>
              {billingMsg}
            </Alert>
          )}
          {billingErr && (
            <Alert severity="error" sx={{ mb: 2 }} onClose={() => setBillingErr(null)}>
              {billingErr}
            </Alert>
          )}
          <Card
            sx={{
              borderRadius: 4,
              border: "1px solid",
              borderColor: "divider",
              bgcolor: "background.paper",
              maxWidth: 720,
              boxShadow: (t) =>
                t.palette.mode === "dark" ? `0 8px 28px ${alpha("#000", 0.35)}` : `0 8px 28px ${alpha("#0f172a", 0.07)}`,
            }}
          >
            <CardContent>
              <Stack spacing={2}>
                <Typography variant="subtitle2" sx={{ fontWeight: 700, color: "text.primary" }}>
                  Ek paket birimi
                </Typography>
                <Typography variant="caption" color="text.secondary" sx={{ display: "block", mt: -0.5 }}>
                  Checkout’ta satılan ek kapasite: birim fiyat ve birim başına doktor / depo / e-posta.
                </Typography>
                <TextField
                  label="Birim fiyat (₺/ay)"
                  type="number"
                  fullWidth
                  size="small"
                  value={billingForm.addon_price_tl_monthly ?? ""}
                  onChange={(e) =>
                    setBillingForm((f) => ({ ...f, addon_price_tl_monthly: e.target.value }))
                  }
                />
                <TextField
                  label="Birim başına doktor kotası"
                  type="number"
                  fullWidth
                  size="small"
                  inputProps={{ min: 1 }}
                  value={billingForm.addon_doctor_slots_per_unit ?? ""}
                  onChange={(e) =>
                    setBillingForm((f) => ({ ...f, addon_doctor_slots_per_unit: e.target.value }))
                  }
                />
                <TextField
                  label="Birim başına ek GB"
                  type="number"
                  fullWidth
                  size="small"
                  value={billingForm.addon_extra_storage_gb_per_unit ?? ""}
                  onChange={(e) =>
                    setBillingForm((f) => ({
                      ...f,
                      addon_extra_storage_gb_per_unit: e.target.value,
                    }))
                  }
                />
                <TextField
                  label="Birim başına ek aylık e-posta"
                  type="number"
                  fullWidth
                  size="small"
                  value={billingForm.addon_extra_email_monthly_per_unit ?? ""}
                  onChange={(e) =>
                    setBillingForm((f) => ({
                      ...f,
                      addon_extra_email_monthly_per_unit: e.target.value,
                    }))
                  }
                />

                <Divider sx={{ my: 1 }} />

                <Typography variant="subtitle2" sx={{ fontWeight: 700, color: "text.primary" }}>
                  Yıllık faturalama
                </Typography>
                <Typography variant="caption" color="text.secondary" sx={{ display: "block", mt: -0.5 }}>
                  Kurum yıllık ödemeyi seçtiğinde referans aylık toplam × 12 üzerinden uygulanır; plan bedeli ve ek paket
                  dahil tüm satıra etki eder (sadece ek pakete özel değildir).
                </Typography>
                <TextField
                  label="Yıllık ödeme indirimi %"
                  type="number"
                  fullWidth
                  size="small"
                  inputProps={{ min: 0, max: 100 }}
                  value={billingForm.yearly_discount_percent ?? ""}
                  onChange={(e) =>
                    setBillingForm((f) => ({ ...f, yearly_discount_percent: e.target.value }))
                  }
                />
                <Button variant="contained" onClick={saveBilling} disabled={billingSaving} sx={{ alignSelf: "flex-start" }}>
                  {billingSaving ? "Kaydediliyor…" : "Platform fiyat ayarlarını kaydet"}
                </Button>
              </Stack>
            </CardContent>
          </Card>
        </Grid>
      </Grid>

      <Snackbar
        open={snackbar.open}
        autoHideDuration={3500}
        onClose={() => setSnackbar((s) => ({ ...s, open: false }))}
        anchorOrigin={{ vertical: "bottom", horizontal: "right" }}
      >
        <Alert
          onClose={() => setSnackbar((s) => ({ ...s, open: false }))}
          severity={snackbar.severity}
          variant="filled"
          sx={{ borderRadius: 2 }}
        >
          {snackbar.message}
        </Alert>
      </Snackbar>
    </Box>
  );
}

